import { Badge, Button, EmptyState, Row } from "@community-project-org/ui";
import { useQuery } from "@tanstack/react-query";
import { UserPlus, Users } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { apiRequest } from "../../common/api/apiClient.js";
import { ROLE_LABELS, type TenantRole } from "../../common/membership/roles.js";
import type { components } from "../../generated/api.js";
import { MemberSheet } from "./MemberSheet.js";
import { scopeSummary } from "./scopeDraft.js";

type TeamMemberDTO = components["schemas"]["TeamMemberDTO"];
type ServiceGroupDTO = components["schemas"]["ServiceGroupDTO"];
type ServiceDTO = components["schemas"]["ServiceDTO"];

/**
 * Das Team: wer welche Rolle hat und wofür. Bearbeiten lässt sich, was die eigene
 * Rolle vergeben darf; das eigene Konto ändert jemand anderes.
 */
export function TeamPage(): JSX.Element {
  const team = useQuery({ queryKey: ["team"], queryFn: () => apiRequest<TeamMemberDTO[]>("/team") });
  const roles = useQuery({ queryKey: ["team", "roles"], queryFn: () => apiRequest<TenantRole[]>("/team/roles") });
  const groups = useQuery({ queryKey: ["service-groups"], queryFn: () => apiRequest<ServiceGroupDTO[]>("/service-groups") });
  const services = useQuery({ queryKey: ["services"], queryFn: () => apiRequest<ServiceDTO[]>("/services") });
  const [editing, setEditing] = useState<TeamMemberDTO | "new" | null>(null);

  const assignable = roles.data ?? [];
  const canEdit = (member: TeamMemberDTO): boolean => !member.isSelf && assignable.includes(member.role);

  return (
    <div className="px-5 pb-8">
      <div className="flex items-center gap-3 pt-5">
        <h1 className="flex-1 text-[22px] font-extrabold tracking-[-0.03em] text-ink">Team</h1>
        {assignable.length > 0 && (
          <Button onClick={() => setEditing("new")}>
            <UserPlus size={16} aria-hidden="true" />
            Einladen
          </Button>
        )}
      </div>

      {team.isPending && <p className="mt-5 text-[14px] text-ink-3">Wird geladen …</p>}
      {team.isError && (
        <p className="mt-5 text-[14px] text-ink-2">Das Team verwaltet nur, wer für alle Angebote freigegeben ist.</p>
      )}
      {team.data?.length === 0 && (
        <EmptyState icon={<Users aria-hidden="true" />} title="Noch niemand im Team" description="Lade Kolleginnen und Kollegen per Mailadresse ein." />
      )}

      <div className="card mt-5 divide-y divide-line">
        {team.data?.map((member) => (
          <Row
            key={member.membershipId}
            title={
              <span className="flex flex-wrap items-center gap-2">
                {member.displayName}
                {member.isSelf && <Badge>Du</Badge>}
                {member.pending && <Badge tone="warning">eingeladen</Badge>}
                {member.status !== "ACTIVE" && <Badge tone="danger">gesperrt</Badge>}
              </span>
            }
            subtitle={`${ROLE_LABELS[member.role].label} · ${scopeSummary(member, groups.data ?? [], services.data ?? [])}`}
            chevron={canEdit(member)}
            disabled={!canEdit(member)}
            {...(canEdit(member) ? { onSelect: () => setEditing(member) } : {})}
          />
        ))}
      </div>

      {editing && (
        <MemberSheet
          member={editing === "new" ? null : editing}
          roles={assignable}
          groups={groups.data ?? []}
          services={services.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
